import React, { useState } from "react"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import {Star, ArrowRight, Filter} from 'lucide-react'

const ProductsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [selectedCapacity, setSelectedCapacity] = useState("all")

  const newProducts = [
    {
      id: 1,
      name: "Mule 628T",
      category: "Flagship Model",
      filterCategory: "flagship",
      capacity: "high",
      image: "https://static.lumi.new/83/8323d6f5b2163d75439767d247c570f8.png",
      price: "Contact for pricing",
      features: ["28,000 lbs capacity", "Hydraulic lift system", "Dual-axle stability", "Remote control operation"],
      featured: true
    },
    {
      id: 2,
      name: "Mule Pro 520",
      category: "Professional Series",
      filterCategory: "professional",
      capacity: "medium",
      image: "https://static.lumi.new/c3/c325335c821a43d843140cc603cc97e1.png",
      price: "Contact for pricing",
      features: ["20,000 lbs capacity", "Compact design", "All-terrain capability", "Quick-hitch system"],
      featured: false
    },
    {
      id: 3,
      name: "Mule Heavy 750",
      category: "Heavy-Duty",
      filterCategory: "heavy-duty",
      capacity: "ultra-high",
      image: "https://static.lumi.new/36/3661be1428baa055a82e994af7fba1ac.png",
      price: "Contact for pricing",
      features: ["35,000 lbs capacity", "Extended boom reach", "Reinforced frame", "Commercial grade"],
      featured: false
    }
  ]

  const usedProducts = [
    {
      id: 4,
      name: "2019 Mule 628T",
      filterCategory: "flagship",
      capacity: "high",
      condition: "Excellent",
      hours: "1,200 hrs",
      image: "https://static.lumi.new/83/8323d6f5b2163d75439767d247c570f8.png",
      price: "Contact for pricing"
    },
    {
      id: 5,
      name: "2020 Mule Pro 520",
      filterCategory: "professional",
      capacity: "medium",
      condition: "Good",
      hours: "2,500 hrs",
      image: "https://static.lumi.new/c3/c325335c821a43d843140cc603cc97e1.png",
      price: "Contact for pricing"
    },
    {
      id: 6,
      name: "2018 Mule Heavy 750",
      filterCategory: "heavy-duty",
      capacity: "ultra-high",
      condition: "Very Good",
      hours: "1,800 hrs",
      image: "https://static.lumi.new/36/3661be1428baa055a82e994af7fba1ac.png",
      price: "Contact for pricing"
    }
  ]

  const filteredNewProducts = newProducts.filter(product => {
    const categoryMatch = selectedCategory === "all" || product.filterCategory === selectedCategory
    const capacityMatch = selectedCapacity === "all" || product.capacity === selectedCapacity
    return categoryMatch && capacityMatch
  })

  const filteredUsedProducts = usedProducts.filter(product => {
    const categoryMatch = selectedCategory === "all" || product.filterCategory === selectedCategory
    const capacityMatch = selectedCapacity === "all" || product.capacity === selectedCapacity
    return categoryMatch && capacityMatch
  })

  return (
    <div className="bg-zinc-900 min-h-screen">
      {/* Header */}
      <section className="bg-zinc-950 py-20 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold text-white mb-6">
              Professional <span className="text-orange-500">Shed Moving Equipment</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Explore comprehensive information about new and pre-owned mule shed equipment. Educational resource for professionals researching equipment options.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Product - Mule 628T */}
      <section className="py-20 bg-gradient-to-br from-zinc-900 to-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-zinc-800 rounded-2xl overflow-hidden border border-orange-500/30 shadow-2xl shadow-orange-500/10">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative h-96 md:h-auto">
                <img 
                  src="https://static.lumi.new/83/8323d6f5b2163d75439767d247c570f8.png"
                  alt="Mule 628T flagship shed moving equipment"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-orange-500 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2">
                  <Star size={20} fill="white" />
                  Featured Model
                </div>
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <h2 className="text-4xl font-bold text-white mb-4">
                  <span className="text-orange-500">Mule 628T</span> - The Industry Standard
                </h2>
                <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                  The flagship <strong>Mule 628T</strong> model represents the industry standard for professional shed moving operations. This informational overview covers its precision hydraulics and heavy-duty construction specifications.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-zinc-900 p-4 rounded-lg">
                    <p className="text-orange-500 font-bold text-2xl">28,000 lbs</p>
                    <p className="text-gray-400 text-sm">Max Capacity</p>
                  </div>
                  <div className="bg-zinc-900 p-4 rounded-lg">
                    <p className="text-orange-500 font-bold text-2xl">Remote</p>
                    <p className="text-gray-400 text-sm">Control System</p>
                  </div>
                  <div className="bg-zinc-900 p-4 rounded-lg">
                    <p className="text-orange-500 font-bold text-2xl">Dual Axle</p>
                    <p className="text-gray-400 text-sm">Stability</p>
                  </div>
                  <div className="bg-zinc-900 p-4 rounded-lg">
                    <p className="text-orange-500 font-bold text-2xl">Hydraulic</p>
                    <p className="text-gray-400 text-sm">Lift System</p>
                  </div>
                </div>
                <Link 
                  to="/blog" 
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2 w-full md:w-auto"
                >
                  Learn More <ArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-zinc-800 border-y border-zinc-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-2 text-white">
              <Filter size={20} className="text-orange-500" />
              <span className="font-bold">Filter Equipment:</span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <div>
                <label htmlFor="category" className="text-gray-400 text-sm block mb-2">Category</label>
                <select
                  id="category"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="bg-zinc-900 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-orange-500 focus:outline-none"
                >
                  <option value="all">All Categories</option>
                  <option value="flagship">Flagship Model</option>
                  <option value="professional">Professional Series</option>
                  <option value="heavy-duty">Heavy-Duty</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="capacity" className="text-gray-400 text-sm block mb-2">Capacity</label>
                <select
                  id="capacity"
                  value={selectedCapacity}
                  onChange={(e) => setSelectedCapacity(e.target.value)}
                  className="bg-zinc-900 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-orange-500 focus:outline-none"
                >
                  <option value="all">All Capacities</option>
                  <option value="medium">20,000 lbs (Medium)</option>
                  <option value="high">28,000 lbs (High)</option>
                  <option value="ultra-high">35,000 lbs (Ultra-High)</option>
                </select>
              </div>

              {(selectedCategory !== "all" || selectedCapacity !== "all") && (
                <button
                  onClick={() => {
                    setSelectedCategory("all")
                    setSelectedCapacity("all")
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-bold transition-colors self-end"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* New Equipment Section */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              New <span className="text-orange-500">Shed Mule for Sale</span> - Equipment Overview
            </h2>
            <p className="text-gray-400 text-lg">
              Information about brand new equipment models, specifications, and typical features
            </p>
          </div>

          {filteredNewProducts.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {filteredNewProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700 hover:border-orange-500 transition-all hover:transform hover:scale-105"
                >
                  <div className="relative h-64">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    {product.featured && (
                      <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        Best Seller
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="text-orange-500 text-sm font-bold mb-2">{product.category}</p>
                    <h3 className="text-2xl font-bold text-white mb-4">{product.name}</h3>
                    <ul className="space-y-2 mb-6">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="text-gray-400 text-sm flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xl font-bold text-white mb-4">{product.price}</p>
                    <Link 
                      to="/blog" 
                      className="block bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-bold text-center transition-colors"
                    >
                      View Details
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No products match your filter criteria. Try adjusting your filters.</p>
            </div>
          )}
        </div>
      </section>

      {/* Used Equipment Section */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              <span className="text-orange-500">Used Shed Mule for Sale</span> - Pre-Owned Guide
            </h2>
            <p className="text-gray-400 text-lg">
              Educational guide about certified pre-owned equipment considerations and typical specifications
            </p>
          </div>

          {filteredUsedProducts.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {filteredUsedProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700 hover:border-orange-500 transition-all hover:transform hover:scale-105"
                >
                  <div className="relative h-64">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4 bg-zinc-900 text-white px-3 py-1 rounded-full text-sm font-bold border border-zinc-700">
                      {product.condition}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{product.name}</h3>
                    <p className="text-gray-400 mb-4">Operating Hours: {product.hours}</p>
                    <p className="text-xl font-bold text-white mb-4">{product.price}</p>
                    <Link 
                      to="/blog" 
                      className="block bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-bold text-center transition-colors"
                    >
                      Read More
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No used equipment matches your filter criteria. Try adjusting your filters.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need More Information?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Explore our comprehensive guides and resources to learn more about finding the perfect mule to move shed for specific requirements and configurations.
          </p>
          <Link 
            to="/blog" 
            className="inline-block bg-white hover:bg-gray-100 text-orange-600 px-10 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl"
          >
            Browse Resources
          </Link>
        </div>
      </section>
    </div>
  )
}

export default ProductsPage